import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { alpha, Box, Drawer, Typography, useTheme } from '@mui/material';
import {
  IconAdjustmentsHorizontal,
  IconTag,
  IconUsers,
} from '@tabler/icons-react';

import {
  Button,
  DateRangeFilterButton,
  elevations,
  IconButton,
  MultiSelectFilter,
  SearchBar,
  SearchWithDropdown,
} from '@zendesk/forethought-elements';
import { FilterOption } from '../analytic-filter/types';
import { GroupedMultiSelectFilterOptions } from '../app/types';
import { BrandIcon } from '../brand-icon/BrandIcon';
import { SearchType } from '../dashboard-pages/discover-all-topics-page/types';
import {
  initialInsightParams,
  INTENT_FILTER_PARAM_NAME,
} from '../dashboard-pages/solve-insights/constants';
import {
  dateRangeToTimestamp,
  isUiQueryError,
} from '../dashboard-pages/solve-insights/helpers';
import { useGetChannelOptionsForSolveInsights } from '../dashboard-pages/solve-insights/hooks/useGetChannelOptions';
import useInsightCardViewInfo from '../dashboard-pages/solve-insights/hooks/useInsightCardViewInfo';
import useProductMode from '../dashboard-pages/solve-insights/hooks/useProductMode';
import {
  InsightsTab,
  ProductMode,
  SearchQueryMode,
} from '../dashboard-pages/solve-insights/types';
import WithHelperText from '../with-helper-text/WithHelperText';
import ContextVariableFilterBuilder from './ContextVariableFilterBuilder';
import EventFilterBuilder from './EventFilterBuilder';
import FilterLabelContainer from './FilterLabelContainer';
import {
  collapseAllChannelsSelection,
  listCVObjectDeserialize,
  listCVObjectSerialize,
  listEventObjectDeserialize,
  listEventObjectSerialize,
} from './helper';
import ListOptionParent from './ListOptionParent';
import { ContextVariableFilterItemValue } from './types';
import xIcon from 'src/assets/images/close-icon.svg';
import chatIcon from 'src/assets/images/icon-message.svg';
import {
  DISCOVER_SHARED_PARAM_NAMES,
  getGroupedMultiSelectFilterOptions,
  initialGroupFilters,
  initialSearchType,
} from 'src/constants/discover';
import { initialSourceFilter } from 'src/constants/discover';
import useOrgConfig from 'src/hooks/discover/useOrgConfig';
import {
  useGetWorkflowTags,
  useHasAiQaAccess,
  useIsInAssist,
  useStateParams,
} from 'src/hooks/hooks';
import useGetTopicOptions from 'src/pages/intent-conversation-analytics/hooks/useGetTopicOptions';
import {
  getOperator,
  isValuelessOperator,
} from 'src/pages/workflow-builder-edit/conditions/conditionEditorHelpers';
import { DiscoverTopicSource } from 'src/reducers/discoverReducer/types';
import { useGetRubricFilterOptionsQuery } from 'src/services/ai-qa/aiQaApi';
import { useGetBrandsQuery } from 'src/services/brand/brandApi';
import { useGetFeatureFlagsQuery } from 'src/services/dashboard-api';
import { EventChartProperty } from 'src/services/insight-cards/types';
import { useGetWorkflowDetailHeaderQuery } from 'src/services/insights';
import {
  HUMAN_FEEDBACK_FILTER_VALUES,
  HumanFeedbackFilterValue,
} from 'src/services/insights/types';
import { Brand } from 'src/types/brandTypes';
import {
  dateRangeDeserialize,
  dateRangeSerialize,
  genericSerializeAndDeserialize,
  groupFiltersValidator,
  listDeserialize,
  listSerialize,
  queryFilterDeserialize,
  queryFilterParameterValidator,
  queryFilterSerialize,
  timeFilterParameterValidator,
} from 'src/utils/discover/helpers';
import { datePickerRangeOptionsRevamp } from 'src/utils/timeRangeHelpers';

// The "Human feedback" group in the All-filters drawer. Its three buckets are
// mutually exclusive over review-eligible conversations and share state with
// the Flagged toggle button (via the `chats_human_feedback` param). Selecting
// all of them (or none) means "no filter", matching the backend.
const getHumanFeedbackFilterOption = (t: TFunction<'discover'>) => ({
  label: t('globalFilterDrawer.humanFeedback.label'),
  options: [
    { label: t('globalFilterDrawer.humanFeedback.flagged'), value: 'flagged' },
    {
      label: t('globalFilterDrawer.humanFeedback.feedbackGiven'),
      value: 'feedback_given',
    },
    {
      label: t('globalFilterDrawer.humanFeedback.noFeedback'),
      value: 'no_feedback',
    },
  ],
  value: 'human_feedback',
});

// Persist a strict, non-empty subset only. All buckets (or none) collapses to
// an empty value so the URL stays clean and the backend reads it as no filter.
const normalizeHumanFeedback = (
  selected: string[],
): HumanFeedbackFilterValue[] =>
  selected.length === 0 ||
  selected.length === HUMAN_FEEDBACK_FILTER_VALUES.length
    ? []
    : (selected as HumanFeedbackFilterValue[]);

interface GlobalFilterDrawerProps {
  filterType: InsightsTab | 'assist';
  handleReset: () => void;
  isOpen: boolean;
  onClose: () => void;
  otherFiltersOptions:
    | ComponentProps<typeof MultiSelectFilter>['options']
    | FilterOption[];
  preserveFilterOrder?: boolean;
  product?: ProductMode;
  searchOptions?: { text: string; value: string }[];
  setSourceFilter?: (
    value: DiscoverTopicSource[],
    batchUpdater?: () => URLSearchParams,
  ) => URLSearchParams;
  setStateParams?: (
    params: Record<string, string>,
    applyParams: () => URLSearchParams,
  ) => void;
  setUiQuery?: (value: string) => void;
  showHumanFeedbackFilter?: boolean;
  sourceFilter?: DiscoverTopicSource[];
  sourceFilterOptions?: Array<{ label: string; value: string }>;
  tab?: InsightsTab | 'assist';
  uiQuery?: string;
  uiQueryMode?: string;
}

const GlobalFilterDrawer = ({
  filterType,
  handleReset,
  isOpen,
  onClose,
  otherFiltersOptions,
  preserveFilterOrder = false,
  product,
  searchOptions,
  setSourceFilter,
  setStateParams,
  setUiQuery,
  showHumanFeedbackFilter = false,
  sourceFilter,
  sourceFilterOptions,
  tab,
  uiQuery = '',
  uiQueryMode = '',
}: GlobalFilterDrawerProps) => {
  const { palette } = useTheme();
  const { t } = useTranslation('discover');
  const groupedMultiSelectFilterOptions = useMemo(
    () => getGroupedMultiSelectFilterOptions(t),
    [t],
  );
  const workflowTags = useGetWorkflowTags();
  const { is_ticket_keyword_search_enabled } = useOrgConfig();
  const [, setQueryFilters] = useStateParams({
    deserialize: queryFilterDeserialize,
    initialState: [],
    paramsName: DISCOVER_SHARED_PARAM_NAMES.QUERY_FILTER,
    serialize: queryFilterSerialize,
    validator: queryFilterParameterValidator(),
  });
  const { workflowId = '' } = useParams<'workflowId'>();

  const { data: featureFlagsData } = useGetFeatureFlagsQuery();
  const { feature_flags: featureFlags = [] } = featureFlagsData ?? {};
  const isVoiceMultiBrandEnabled = featureFlags.includes('voice_multibrand');

  const [productMode] = useProductMode();

  const defaultedProduct = product || productMode;

  const [expandedValues, setExpandedValues] = useState<string[]>([]);
  const isEmail = defaultedProduct === 'email';
  const isVoice = defaultedProduct === 'voice';
  const isAssist = defaultedProduct === 'assist';
  const isApi = defaultedProduct === 'api';
  const isSlack = defaultedProduct === 'slack';

  // Fetch voice brands for brand filter
  const { data: voiceBrands = [] } = useGetBrandsQuery(
    {
      brandType: 'solve-voice',
    },
    { skip: !isVoice || !isVoiceMultiBrandEnabled },
  );

  // Format brands data for filter options
  const brandFilterOptions = useMemo(() => {
    return voiceBrands.map((brand: Brand, index: number) => ({
      index,
      label: brand.brand_name,
      options: brand.tags.map((tag: string) => ({
        label: tag,
        value: `voice_filter_number.${tag}`,
      })),
      optionStartAdornment: <BrandIcon index={index} variant='small' />,
      value: brand.brand_name,
    }));
  }, [voiceBrands]);

  // Fetch rubric filter options for chats tab when user has AI QA permission
  const hasAiQaPermission = useHasAiQaAccess();
  const isChatsTab = tab === 'chat';
  const { data: rubricFilterOptionsData } = useGetRubricFilterOptionsQuery(
    undefined,
    {
      skip: !hasAiQaPermission || !isChatsTab,
    },
  );

  // Format rubric filter options for the filter dropdown
  const rubricFilterOptions = useMemo(() => {
    if (!rubricFilterOptionsData?.data) {
      return [];
    }
    return rubricFilterOptionsData.data.map(rubric => ({
      label: rubric.label,
      options: rubric.values.map(item => ({
        label: item.label,
        value: `rubric_filters.${rubric.model_id}.${item.value}`,
      })),
      value: rubric.model_id,
    }));
  }, [rubricFilterOptionsData]);

  // state params
  const [dateRange, setDateRange] = useStateParams({
    deserialize: dateRangeDeserialize,
    initialState: initialInsightParams.date,
    paramsName: 'date',
    serialize: dateRangeSerialize,
    validator: timeFilterParameterValidator([]),
  });
  const [searchQueryMode, setSearchQueryMode] = useStateParams<SearchQueryMode>(
    {
      deserialize: genericSerializeAndDeserialize as (
        str: string,
      ) => SearchQueryMode,
      initialState: 'conversation_id',
      paramsName: 'chats_search_mode',
      serialize: genericSerializeAndDeserialize,
    },
  );
  const [channels, setChannels] = useStateParams<string[]>({
    deserialize: listDeserialize,
    initialState: [],
    paramsName: 'channels',
    serialize: listSerialize,
  });
  const [multiSelected, setMultiSelected] = useStateParams<string[]>({
    deserialize: listDeserialize,
    initialState: [],
    paramsName: `${INTENT_FILTER_PARAM_NAME}${tab === 'assist' ? '_assist' : ''}`,
    serialize: listSerialize,
  });
  const [cvFilters, setCvFilters] = useStateParams<
    ContextVariableFilterItemValue[]
  >({
    deserialize: listCVObjectDeserialize,
    initialState: [],
    paramsName: `${INTENT_FILTER_PARAM_NAME}_cv`,
    serialize: listCVObjectSerialize,
  });
  const [eventFilters, setEventFilters] = useStateParams<EventChartProperty[]>({
    deserialize: listEventObjectDeserialize,
    initialState: [],
    paramsName: `${INTENT_FILTER_PARAM_NAME}_event`,
    serialize: listEventObjectSerialize,
  });
  // Ui state management
  const initialTags = multiSelected.filter(item =>
    item.includes('workflow_tags'),
  );
  const initialBrands = multiSelected.filter(item =>
    item.includes('voice_filter_number'),
  );
  const [uiDateRange, setUiDateRange] = useState(dateRange);
  const [uiMultiSelected, setUiMultiSelected] = useState(multiSelected);
  const [uiChannels, setUiChannels] = useState(channels);
  const [uiTags, setUiTags] = useState(initialTags);
  const [uiBrands, setUIBrands] = useState(initialBrands);
  const [uiSearchQuery, setUiSearchQuery] = useState(uiQuery);
  const [uiSearchQueryMode, setUiSearchQueryMode] = useState<string>(
    uiQueryMode || searchQueryMode,
  );
  const [uiCvFilters, setUICvFilters] =
    useState<ContextVariableFilterItemValue[]>(cvFilters);
  const [uiEventFilters, setUIEventFilters] =
    useState<EventChartProperty[]>(eventFilters);
  // Convert source filter to array for multi-select UI
  const getInitialSourceFilterArray = (): string[] => {
    // If no sourceFilter provided, select all available sources
    if (!sourceFilter || sourceFilter.length === 0) {
      return (
        sourceFilterOptions
          ?.filter(opt => opt.value !== 'all')
          .map(opt => opt.value) ?? []
      );
    }
    // If sourceFilter contains all available sources, treat as "all selected"
    const availableSources =
      sourceFilterOptions
        ?.filter(opt => opt.value !== 'all')
        .map(opt => opt.value) ?? [];
    const allSelected =
      availableSources.length > 0 &&
      availableSources.every(s =>
        sourceFilter.includes(s as DiscoverTopicSource),
      );
    if (allSelected) {
      return availableSources;
    }
    // Return the sourceFilter array as-is
    return [...sourceFilter];
  };
  const [uiSourceFilter, setUiSourceFilter] = useState<string[]>(
    getInitialSourceFilterArray(),
  );

  // Reset uiSourceFilter when drawer opens to sync with current sourceFilter prop
  useEffect(() => {
    if (isOpen) {
      setUiSourceFilter(getInitialSourceFilterArray());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sourceFilter, sourceFilterOptions]);

  const [groupFilters, setGroupFilters] = useStateParams<
    GroupedMultiSelectFilterOptions[]
  >({
    deserialize: listDeserialize,
    initialState: initialGroupFilters,
    paramsName: DISCOVER_SHARED_PARAM_NAMES.GROUP_FILTERS,
    serialize: listSerialize,
    validator: groupFiltersValidator(),
  });

  const [uiGroupFilters, setUIGroupFilters] =
    useState<GroupedMultiSelectFilterOptions[]>(groupFilters);

  // Human-feedback filter. Shares the `chats_human_feedback` param with the
  // Flagged toggle button in ChatsTable, so toggling either stays in sync.
  const [humanFeedback, setHumanFeedback] = useStateParams<
    HumanFeedbackFilterValue[]
  >({
    deserialize: value =>
      listDeserialize(value).filter((v): v is HumanFeedbackFilterValue =>
        HUMAN_FEEDBACK_FILTER_VALUES.includes(v as HumanFeedbackFilterValue),
      ),
    initialState: [],
    paramsName: 'chats_human_feedback',
    serialize: listSerialize,
  });
  const humanFeedbackParamKey = humanFeedback.join(',');
  const [uiHumanFeedback, setUiHumanFeedback] =
    useState<string[]>(humanFeedback);

  // Re-sync the staged selection when the drawer (re)opens or the committed
  // value changes externally (e.g. via the Flagged toggle button). Mirror the
  // committed state directly so an empty filter renders as no boxes checked.
  useEffect(() => {
    if (isOpen) {
      setUiHumanFeedback(humanFeedback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, humanFeedbackParamKey]);

  const firstSelectedIcon = useMemo(() => {
    return groupedMultiSelectFilterOptions.find(
      option => option.value === uiGroupFilters[0],
    )?.optionStartAdornment;
  }, [uiGroupFilters, groupedMultiSelectFilterOptions]);
  const [searchType, setSearchType] = useStateParams<SearchType>({
    deserialize: item =>
      item !== 'topic' && item !== 'ticket' ? 'topic' : item,
    initialState: initialSearchType,
    paramsName: DISCOVER_SHARED_PARAM_NAMES.SEARCH_TYPE,
    serialize: item => item,
  });

  const topicOptions = useGetTopicOptions({
    dateRange: uiDateRange,
    skip: isEmail || isVoice,
  });
  otherFiltersOptions = [
    ...otherFiltersOptions,
    ...(!isAssist ? topicOptions : []),
    ...(isChatsTab && hasAiQaPermission ? rubricFilterOptions : []),
  ];

  const channelOptions = useGetChannelOptionsForSolveInsights(false, tab);
  // Empty selection means "All Channels"; render every toggle checked in that
  // case so the UI matches the label instead of showing an empty control.
  const allChannelValues = useMemo(
    () => channelOptions.map(option => option.value),
    [channelOptions],
  );

  const backendReadyTimestamps = dateRangeToTimestamp(dateRange);
  const { data: workflowHeaderData } = useGetWorkflowDetailHeaderQuery(
    {
      end: backendReadyTimestamps.end_timestamp,
      start: backendReadyTimestamps.start_timestamp,
      workflow_id: workflowId,
    },
    { skip: tab !== 'workflow' || !workflowId },
  );

  const cleanMultiSelected = useMemo(() => {
    return uiMultiSelected.filter(
      item =>
        !item.includes('workflow_tags') &&
        !item.includes('voice_filter_number'),
    );
  }, [uiMultiSelected]);

  const uiQueryError = isUiQueryError({
    searchQueryMode: uiSearchQueryMode,
    uiQuery: uiSearchQuery,
  });

  const handleDisableApply = useCallback(() => {
    return (
      uiCvFilters.some(
        filter =>
          filter.expression.operator !== 'empty' &&
          filter.expression.values?.length === 0,
      ) ||
      uiEventFilters.some(uiEventFilter =>
        uiEventFilter.filters?.some(
          filter =>
            !filter.expression.field ||
            (!isValuelessOperator(getOperator(filter.expression)) &&
              filter.expression.values?.length === 0),
        ),
      )
    );
  }, [uiEventFilters, uiCvFilters]);

  const isDisabled = handleDisableApply();

  const { isInsightCardView, isMultipleChannels, isVoiceOnly } =
    useInsightCardViewInfo();

  const isInAssist = useIsInAssist();

  const commonFilters = useMemo(() => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            padding: '0px 1px',
          }}
        >
          <FilterLabelContainer label={t('globalFilterDrawer.dateLabel')}>
            <DateRangeFilterButton
              explicitLabel={true}
              initialValue={initialInsightParams.date}
              onChange={value => setUiDateRange(value)}
              options={datePickerRangeOptionsRevamp}
              size='medium'
              value={uiDateRange}
            />
          </FilterLabelContainer>
          {(isInsightCardView ||
            (!isEmail &&
              !isVoice &&
              !isAssist &&
              !isApi &&
              !isSlack &&
              (tab === 'workflow' && workflowId
                ? workflowHeaderData?.channel === 'widget'
                : true))) &&
            filterType !== 'topic' &&
            channelOptions.length > 1 && (
              <FilterLabelContainer
                label={t('globalFilterDrawer.channelLabel')}
              >
                <MultiSelectFilter
                  fullWidth
                  icon={<img src={chatIcon} />}
                  maxHeight={425}
                  onChange={value => setUiChannels(value)}
                  options={channelOptions}
                  placeholder={t('globalFilterDrawer.placeholderAllChannels')}
                  value={uiChannels.length > 0 ? uiChannels : allChannelValues}
                  variant='secondary'
                />
              </FilterLabelContainer>
            )}

          {!isEmail && !isVoice && filterType === 'topic' && (
            <FilterLabelContainer label={t('globalFilterDrawer.showOnlyLabel')}>
              <MultiSelectFilter
                fullWidth
                icon={
                  firstSelectedIcon ?? (
                    <IconAdjustmentsHorizontal
                      color={palette.grey[800]}
                      size={20}
                    />
                  )
                }
                onChange={value => setUIGroupFilters(value)}
                options={groupedMultiSelectFilterOptions}
                placeholder={t('globalFilterDrawer.placeholderAll')}
                value={uiGroupFilters}
                variant='secondary'
              />
            </FilterLabelContainer>
          )}
          {!isEmail &&
            !isVoice &&
            !isAssist &&
            (isInsightCardView
              ? !isMultipleChannels && !isVoiceOnly
              : true) && (
              <FilterLabelContainer label={t('globalFilterDrawer.tagLabel')}>
                <MultiSelectFilter
                  fullWidth
                  icon={<IconTag size={20} />}
                  maxHeight={425}
                  onChange={value => setUiTags(value)}
                  options={[
                    ...workflowTags.map(tag => ({
                      label: tag,
                      value: `workflow_tags.${tag}`,
                    })),
                    {
                      label: t('globalFilterDrawer.naLabel'),
                      value: 'workflow_tags.null',
                    },
                  ]}
                  placeholder={t('globalFilterDrawer.placeholderAllTags')}
                  value={uiTags}
                  variant='secondary'
                />
              </FilterLabelContainer>
            )}
          {isVoice &&
            isVoiceMultiBrandEnabled &&
            (isInsightCardView ? isVoiceOnly : true) &&
            brandFilterOptions &&
            brandFilterOptions.length > 0 && (
              <FilterLabelContainer label={t('globalFilterDrawer.brandLabel')}>
                <MultiSelectFilter
                  fullWidth
                  icon={<IconUsers size={20} />}
                  maxHeight={425}
                  onChange={value => setUIBrands(value)}
                  options={brandFilterOptions}
                  placeholder={t('globalFilterDrawer.placeholderAllBrands')}
                  value={uiBrands}
                  variant='secondary'
                />
              </FilterLabelContainer>
            )}
          {!isAssist &&
            filterType !== 'topic' &&
            (isInsightCardView ? !isMultipleChannels : true) && (
              <ContextVariableFilterBuilder
                filters={uiCvFilters}
                onChange={setUICvFilters}
                product={defaultedProduct}
                uiDateRange={uiDateRange}
              />
            )}
          {isInsightCardView && !isMultipleChannels && !isInAssist && (
            <EventFilterBuilder
              filters={uiEventFilters}
              onChange={setUIEventFilters}
              uiDateRange={uiDateRange}
            />
          )}
          <ListOptionParent
            expandedValues={expandedValues}
            extraOptions={
              showHumanFeedbackFilter
                ? [getHumanFeedbackFilterOption(t)]
                : undefined
            }
            extraValue={showHumanFeedbackFilter ? uiHumanFeedback : undefined}
            onChange={value => setUiMultiSelected(value)}
            onExtraChange={
              showHumanFeedbackFilter ? setUiHumanFeedback : undefined
            }
            onSourceFilterChange={
              filterType === 'topic' ? setUiSourceFilter : undefined
            }
            options={otherFiltersOptions}
            preserveOrder={preserveFilterOrder}
            setExpandedValues={setExpandedValues}
            sourceFilter={filterType === 'topic' ? uiSourceFilter : undefined}
            sourceFilterOptions={
              filterType === 'topic' ? sourceFilterOptions : undefined
            }
            value={cleanMultiSelected}
          />
        </Box>
        <Box
          sx={{
            bgcolor: 'white',
            bottom: '0',
            display: 'flex',
            gap: '8px',
            marginTop: 'auto',
            padding: '8px 0px',
            position: 'sticky',
          }}
        >
          <Button
            onClick={() => {
              handleReset();
              onClose();
            }}
            size='medium'
            variant='ghost'
          >
            <Typography noWrap variant='font14Medium'>
              {t('globalFilterDrawer.reset')}
            </Typography>
          </Button>
          <Button
            disabled={isDisabled}
            fullWidth
            onClick={() => {
              const combinedMultiSelected = [
                ...cleanMultiSelected,
                ...uiTags,
                ...uiBrands,
              ];
              setUiQuery && setUiQuery(uiSearchQuery);

              // Convert UI source filter to the format expected by the parent
              // If no sources selected, default to all sources
              const computedSourceFilter: DiscoverTopicSource[] =
                uiSourceFilter.length === 0
                  ? initialSourceFilter
                  : (uiSourceFilter as DiscoverTopicSource[]);

              const normalizedHumanFeedback =
                normalizeHumanFeedback(uiHumanFeedback);

              const channelsToApply = collapseAllChannelsSelection(
                uiChannels,
                allChannelValues,
              );

              const applyParams = () =>
                setDateRange(uiDateRange, () =>
                  setMultiSelected(combinedMultiSelected, () =>
                    setChannels(channelsToApply, () =>
                      setSearchQueryMode(
                        uiSearchQueryMode as SearchQueryMode,
                        () =>
                          setCvFilters(uiCvFilters, () =>
                            setEventFilters(uiEventFilters, () => {
                              const applyGroupFilters = () =>
                                setGroupFilters(uiGroupFilters, () => {
                                  // Include sourceFilter in the batch update chain
                                  if (setSourceFilter) {
                                    return setSourceFilter(
                                      computedSourceFilter,
                                      () =>
                                        setQueryFilters(combinedMultiSelected),
                                    );
                                  }
                                  return setQueryFilters(combinedMultiSelected);
                                });
                              // Only commit human feedback when the section is
                              // shown, so other drawer usages don't clobber it.
                              return showHumanFeedbackFilter
                                ? setHumanFeedback(
                                    normalizedHumanFeedback,
                                    applyGroupFilters,
                                  )
                                : applyGroupFilters();
                            }),
                          ),
                      ),
                    ),
                  ),
                );

              if (setStateParams) {
                setStateParams(
                  {
                    uiQuery: uiSearchQuery,
                    uiQueryMode: uiSearchQueryMode as string,
                  },
                  applyParams,
                );
              } else {
                applyParams();
              }
              onClose();
            }}
            size='medium'
            variant='main'
          >
            <Typography noWrap variant='font14Medium'>
              {t('globalFilterDrawer.apply')}
            </Typography>
          </Button>
        </Box>
      </Box>
    );
  }, [
    uiDateRange,
    isInsightCardView,
    isEmail,
    isVoice,
    isAssist,
    isApi,
    isSlack,
    tab,
    workflowId,
    workflowHeaderData?.channel,
    filterType,
    channelOptions,
    allChannelValues,
    uiChannels,
    firstSelectedIcon,
    groupedMultiSelectFilterOptions,
    t,
    palette.grey,
    uiGroupFilters,
    isMultipleChannels,
    isVoiceOnly,
    workflowTags,
    uiTags,
    isVoiceMultiBrandEnabled,
    brandFilterOptions,
    uiBrands,
    uiCvFilters,
    defaultedProduct,
    isInAssist,
    uiEventFilters,
    expandedValues,
    otherFiltersOptions,
    preserveFilterOrder,
    cleanMultiSelected,
    isDisabled,
    handleReset,
    onClose,
    setUiQuery,
    uiSearchQuery,
    setStateParams,
    setDateRange,
    setMultiSelected,
    setChannels,
    setSearchQueryMode,
    uiSearchQueryMode,
    setCvFilters,
    setEventFilters,
    setGroupFilters,
    setQueryFilters,
    sourceFilterOptions,
    uiSourceFilter,
    setSourceFilter,
    showHumanFeedbackFilter,
    uiHumanFeedback,
    setHumanFeedback,
  ]);

  const searchBar = useMemo(() => {
    if (filterType === 'article') {
      return (
        <Box
          sx={{
            width: '360px',
          }}
        >
          <SearchBar
            onChange={e => setUiSearchQuery(e.target.value)}
            placeholder={t('globalFilterDrawer.searchArticlePlaceholder')}
            size='small'
            value={uiSearchQuery}
          />
        </Box>
      );
    }

    if (searchOptions) {
      return (
        <Box
          sx={{
            width: '360px',
          }}
        >
          <WithHelperText helperText={uiQueryError}>
            <SearchWithDropdown
              aria-label={t('globalFilterDrawer.chatIdAriaLabel')}
              fullWidth
              onChange={e => setUiSearchQuery(e.target.value)}
              onClear={() => setUiSearchQuery('')}
              options={[...searchOptions]}
              placeholder={t('globalFilterDrawer.searchEnterPlaceholder', {
                mode: searchOptions
                  .find(option => option.value === uiSearchQueryMode)
                  ?.text.toLowerCase(),
              })}
              selectChange={value => {
                setUiSearchQueryMode(value as SearchQueryMode);
                setUiSearchQuery('');
              }}
              selectedOption={uiSearchQueryMode}
              value={uiSearchQuery}
            />
          </WithHelperText>
        </Box>
      );
    }

    if (filterType === 'chat') {
      return (
        <Box
          sx={{
            width: '360px',
          }}
        >
          <SearchBar
            onChange={e => setUiSearchQuery(e.target.value)}
            placeholder={t('globalFilterDrawer.conversationIdPlaceholder')}
            size='small'
            value={uiSearchQuery}
          />
        </Box>
      );
    }
    if (filterType === 'topic') {
      return (
        <Box
          sx={{
            width: '360px',
          }}
        >
          <SearchWithDropdown
            onChange={e => setUiSearchQuery(e.target.value)}
            onClear={() => setUiSearchQuery('')}
            options={[
              {
                text: t('globalFilterDrawer.searchTypeOption.topic'),
                value: 'topic',
              },
              ...(is_ticket_keyword_search_enabled
                ? [
                    {
                      text: t('globalFilterDrawer.searchTypeOption.ticket'),
                      value: 'ticket',
                    },
                  ]
                : []),
            ]}
            placeholder={
              searchType === 'topic'
                ? t('globalFilterDrawer.searchForTopicPlaceholder')
                : t('globalFilterDrawer.searchForTicketPlaceholder')
            }
            selectChange={value => {
              setSearchType(value as 'topic' | 'ticket');
            }}
            selectedOption={searchType}
            value={uiSearchQuery}
          />
        </Box>
      );
    }

    return null;
  }, [
    filterType,
    searchOptions,
    uiQueryError,
    uiSearchQuery,
    uiSearchQueryMode,
    searchType,
    setSearchType,
    is_ticket_keyword_search_enabled,
    t,
  ]);

  return (
    <Drawer
      anchor='right'
      ModalProps={{
        BackdropProps: {
          sx: {
            bgcolor: alpha(palette.colors.slate[800], 0.2),
          },
        },
      }}
      onClose={onClose}
      open={isOpen}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            boxShadow: elevations.z5,
            width: '400px',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          height: '100%',
          padding: '24px',
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='font18Bold'>
            {t('globalFilterDrawer.title')}
          </Typography>
          <IconButton
            aria-label={t('globalFilterDrawer.closeAriaLabel')}
            onClick={onClose}
            variant='ghost'
          >
            <img src={xIcon} />
          </IconButton>
        </Box>
        {searchBar}
        {commonFilters}
      </Box>
    </Drawer>
  );
};

export default GlobalFilterDrawer;
